import { defineAsyncComponent } from 'vue'
import { SetupCalendar } from 'v-calendar'
import { plugin as VueTippy } from 'vue-tippy'
import { START_LOCATION } from 'vue-router'
import { useNotyf } from './composable/useNotyf'
import { useUserSession } from './stores/userSession'
import { browserLanguage } from './stores/personalAreaInfo'
import type { VueroAppContext } from './app'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import {
  useClientColors,
  useClientCAColors,
  usePartnerColors,
} from '/@src/composable/useThemeColors'

// Lazy load aditional components
export async function registerGlobalComponents({ app }: VueroAppContext) {
  const background = (await import('./directives/background')).default
  const tooltip = (await import('./directives/tooltip')).default

  app.use(SetupCalendar, {})
  app.use(VueTippy, {
    component: 'Tippy',
    defaultProps: {
      theme: 'light',
    },
  })

  app.component(
    // eslint-disable-next-line vue/multi-word-component-names
    'Multiselect',
    defineAsyncComponent({
      loader: () => import('@vueform/multiselect').then((mod) => mod.default),
      delay: 0,
      suspensible: false,
    })
  )
  app.component(
    // eslint-disable-next-line vue/multi-word-component-names
    'Slider',
    defineAsyncComponent({
      loader: () => import('@vueform/slider').then((mod) => mod.default),
      delay: 0,
      suspensible: false,
    })
  )
  app.component(
    'VCalendar',
    defineAsyncComponent({
      loader: () => import('v-calendar').then((mod) => mod.Calendar),
      delay: 0,
      suspensible: false,
    })
  )
  app.component(
    'VDatePicker',
    defineAsyncComponent({
      loader: () => import('v-calendar').then((mod) => mod.DatePicker),
      delay: 0,
      suspensible: false,
    })
  )
  app.component('EasyDataTable', Vue3EasyDataTable)

  app.directive('background', background)
  app.directive('tooltip', tooltip)
}

/**
 * Here we are setting up two router navigation guards
 *
 * We can add meta to pages either by declaring them manualy in the
 * routes declaration (see /@src/router.ts)
 * or by adding a <route> tag into .vue files (see /@src/pages/sidebar/dashboards.ts)
 *
 * <route lang="yaml">
 * meta:
 *   requiresAuth: true
 * </route>
 *
 * <script setup lang="ts">
 *  // TS script
 * </script>
 *
 * <template>
 *  // HTML content
 * </template>
 */
export function registerRouterNavigationGuards({ router }: VueroAppContext) {
  router.beforeEach(async (to, from) => {
    const userSession = useUserSession()
    const notyf = useNotyf()
    const lang = browserLanguage[0]

    if (from === START_LOCATION && userSession.isLoggedIn) {
      // 1. If the name is not set, it means we are navigating to the first page
      // and we are logged in, so we should check user information from the server
      try {
        // Do api request call to retreive user profile.
        // Note that the api is provided with json-server
        const user = await userSession.getUser()
        const requiredAdmin = to.meta.requiredAdmin
        const allowedRoles = ['employee', 'admin']
        if (requiredAdmin && !allowedRoles.includes(user.role)) {
          return {
            path: '404',
            query: { redirect: to.fullPath },
          }
        }
      } catch (err) {
        // delete stored token if it fails
        userSession.logoutUser()
        notyf.error(lang === 'es' ? 'Tu sesión no es válida' : 'Your session is invalid')
        if (to.meta.requiresAuth) {
          // redirect the user somewhere
          return {
            // Will follow the redirection set in /@src/pages/auth/index.vue
            name: 'auth',
            // save the location we were at to come back later
            query: { redirect: to.fullPath },
          }
        }
      }
    } else if (to.meta.requiresAuth && !userSession.isLoggedIn) {
      // 2. If the requires auth via requiresAuth meta, check if user is logged in
      // if not, redirect to login page.
      notyf.error({
        message:
          lang === 'es'
            ? 'Lo sentimos, debe iniciar sesión para acceder a esta sección'
            : 'Sorry, you must be logged in to access this section.',
        duration: 5000,
      })

      return {
        // Will follow the redirection set in /@src/pages/auth/index.vue
        name: 'auth',
        // save the location we were at to come back later
        query: { redirect: to.fullPath },
      }
    }
  })
  router.afterEach(async (to) => {
    const userSession = useUserSession()
    //color theme configuration
    if (to.path === '/obtener-oferta') {
      useClientColors()
    } else if (to.path === '/oferta-partners') {
      usePartnerColors()
    } else if (to.path === '/oferta-compra-ahora') {
      useClientCAColors()
    } else if (userSession.isLoggedIn) {
      const user = await userSession.getUser()
      if (user.role === 'client-ca') {
        useClientCAColors()
      } else if (user.role === 'partner') {
        usePartnerColors()
      } else {
        useClientColors()
      }
    }
  })
}
